import { useEffect, useState } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Loader2 } from 'lucide-react';

export function OrganizationGuard() {
  const { user, isAuthenticated, isEmailVerified, loading } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [hasOrganization, setHasOrganization] = useState(false);

  useEffect(() => {
    const checkOrganization = async () => {
      if (!isAuthenticated || !user) {
        setIsLoading(false);
        return;
      }

      try {
        // Check if user has an organization_id
        const { data, error } = await supabase
          .from('users')
          .select('organization_id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error checking organization:', error);
          setIsLoading(false);
          return;
        }

        // If user doesn't have an organization, redirect to registration
        if (!data?.organization_id) {
          if (!isEmailVerified) {
            // If not verified, go to verification page
            navigate('/auth/verify-email');
          } else {
            // If verified but no organization, go to register page
            navigate('/auth/register');
          }
          return;
        }

        setHasOrganization(true);
        setIsLoading(false);
      } catch (error) {
        console.error('Failed to check organization status:', error);
        setIsLoading(false);
      }
    };

    if (!loading) {
      checkOrganization();
    }
  }, [user, isAuthenticated, isEmailVerified, loading, navigate]);

  if (loading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // User must have an organization to access protected routes
  if (!hasOrganization) {
    return null;
  }

  return <Outlet />;
} 